<template>
  <v-layout>
    <v-flex v-if="reportsPending">
      <v-flex>
        Getting billing entries...
        <v-progress-linear indeterminate></v-progress-linear>
      </v-flex>
    </v-flex>
    <v-flex v-else>
      <v-layout column full-width>
        <v-layout row align-center>
          <h1 class="display-1 mr-2">Billing Period:</h1>
          <template>
            <v-flex>
              <v-menu
                ref="startDateMenu"
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template slot="activator" slot-scope="{ on }">
                  <v-text-field
                    label="Start Date"
                    format="dddd, MM DD YYYY"
                    @change="value => startDate = value"
                    :value="dateFormatted(startDate)"
                    prepend-icon="event"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :show-current="endDate"
                  v-model="startDate"
                  @input="startDateMenu = false"
                  no-title
                  scrollable
                >
                </v-date-picker>
              </v-menu>
            </v-flex>
          </template>
          <template>
            <v-flex>
              <v-menu
                ref="endDateMenu"
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template slot="activator" slot-scope="{ on }">
                  <v-text-field
                    label="End Date"
                    format="dddd, MM DD YYYY"
                    @change="value => endDate = value"
                    :value="dateFormatted(endDate)"
                    prepend-icon="event"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :show-current="startDate"
                  v-model="endDate"
                  @input="endDateMenu = false"
                  no-title
                  scrollable
                >
                </v-date-picker>
              </v-menu>
            </v-flex>
          </template>
          <v-btn @click="reGetRecords" color="primary">
            Change Dates
          </v-btn>
        </v-layout>

        <!--
        <v-layout row justify-center align-center class="text-xs-center mb-2">
          <v-flex>
            <v-pagination
              v-model="page"
              :length="pageLength"
            >
            </v-pagination>
          </v-flex>
        </v-layout>
        -->

        <v-layout row justify-end align-center class="mb-2">
          <v-flex shrink>
            <v-btn
              disabled
              @click="csvExport"
              color="primary"
              :disabled="loadingCsv"
              :loading="loadingCsv"
            >
              <v-icon left small>
                save_alt
              </v-icon>
              Export all pages
            </v-btn>
          </v-flex>
        </v-layout>

        <v-card>
          <v-card-title>
            <h1 class="subheading-1">Billing</h1>
            <v-layout
              row
              justify-space-around
              align-center
              v-if="user.clientAdmin"
            >
              <v-flex>
                <v-layout column full-width> </v-layout>
              </v-flex>

              <v-flex>
                <span class="subtle caption">Dif Key</span>
                <ul class="subtle caption">
                  <li>
                    Base: M-Th Day
                  </li>
                  <li>
                    Dif1: M-Th Swing
                  </li>
                  <li>
                    Dif2: F-Sun Day
                  </li>
                  <li>
                    Dif3: F-Sun Swing
                  </li>
                  <li>
                    Dif4: M-Th Overnight
                  </li>
                  <li>
                    Dif5: F-Sun Overnight
                  </li>
                </ul>
              </v-flex>
            </v-layout>
          </v-card-title>

          <v-card-text>
            <div v-if="clientAdmins.length > 0" class="mb-4">
              <v-layout row>
                <v-flex class="headline">
                  Client Admins:
                </v-flex>
              </v-layout>

              <v-layout row>
                <v-flex xs="4" v-for="location in clientAdmins">
                  <div class="body-2">
                    {{ location.location_name }}
                  </div>
                  <div v-for="admin in location.admins">
                    <div class="body">
                      {{ admin.name }}
                    </div>
                    <div class="body">
                      {{ admin.email }}
                    </div>
                    <div
                      style="white-space: pre-line;"
                      v-html="admin.billing_details"
                    ></div>
                  </div>
                </v-flex>
              </v-layout>
            </div>

            <div v-for="business in shiftsByBusiness" class="mb-5">
              <v-layout column>
                <v-flex class="mb-2">
                  <v-layout row class="report-row">
                    <v-flex>
                      <h2 class="display-1 mb-2">
                        {{ business.name }}
                      </h2>
                      <div v-for="location in business.locations">
                        <h2 class="subheading-2 font-weight-medium">
                          {{ location.name }}
                        </h2>
                        <h3 class="subheading">Staffing</h3>

                        <div
                          v-for="date in dateOrderedShifts(location.shifts)"
                          class="mb-2 pl-4"
                        >
                          <h3 class="body-3">
                            {{ date.dateObject | moment("dddd MMMM D, YYYY") }}
                          </h3>
                          <div v-for="shift in date.shifts" class="pb-3 pl-4">
                            {{ shift.start_datetime | moment("h:mma") }} -
                            <span v-if="endsNextDay(shift)">
                              {{ shift.end_datetime | moment("ddd h:mma") }}
                            </span>
                            <span v-else>
                              {{ shift.end_datetime | moment("h:mma") }}
                            </span>
                            <span class="px-2">
                              <router-link
                                :to=" { name: 'employeeDetail', params: { employeeId: shift.worker_id } }"
                                >{{ shift.worker }}</router-link
                              >
                            </span>
                            <router-link
                              :to="{ name: 'shiftDetail', params: { shiftId: shift.id } }"
                              >{{ shift.position }}</router-link
                            >
                            <br />
                            <div class="subtle">
                              ({{ shift.clock_in_datetime | moment("h:mma") }} -
                              {{ shift.clock_out_datetime | moment("h:mma") }})
                            </div>
                            <div>
                              <span>
                                <v-chip
                                  pill
                                  small
                                  color="blue-grey darken--4"
                                  text-color="white"
                                >
                                  <v-avatar left>
                                    <v-icon>schedule</v-icon>
                                  </v-avatar>
                                  <strong>{{ shift.hours_worked }}</strong>
                                </v-chip>
                              </span>
                              <span v-if="shift.dif">
                                <v-chip small text-color="blue-grey darken--3"
                                  >{{ shift.dif }}</v-chip
                                >
                              </span>
                              <div v-if="shift.bonuses.length > 0">
                                <ul class="pl-2 unstyled">
                                  <BonusList :shift="shift" :role="user.role" />
                                  <li
                                    v-for="bonus in shift.bonuses"
                                    class="inline-list pr-4"
                                  >
                                    <v-icon>{{ bonusIcon(bonus.type) }}</v-icon>
                                    <span
                                      style="position: relative; bottom: 3px;"
                                      >${{ bonus.amount }}</span
                                    >
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </div>
          </v-card-text>
        </v-card>
      </v-layout>

      <!--
      <v-layout row justify-center align-center class="text-xs-center mt-4">
        <v-flex>
          <v-pagination
            v-model="page"
            :length="pageLength"
          >
          </v-pagination>
        </v-flex>
      </v-layout>
      -->
    </v-flex>
  </v-layout>
</template>

<script>
  import {mapState, mapGetters} from 'vuex';
  import {ExportToCsv} from 'export-to-csv';

  export default {
    components: {},
    computed: {
      ...mapState('userAuth', ['user']),
      //computedOffset: function () {
      //  return this.limit * (this.page - 1)
      //},
      //pageLength: function () {
      //  return Math.ceil(this.queryTotal / this.limit)
      //},
      formattedQuery: function() {
        return {
          startDate: this.startDate,
          endDate: this.endDate,
          //limit: this.limit,
          //offset: this.computedOffset,
          //page: this.page,
        };
      },
      shiftsByBusiness: function() {
        let businesses = [];
        this.shifts.forEach(s => {
          if (
            // haven't seen this business yet, create it.
            businesses.filter(b => b.id == s.client_business_id).length < 1
          ) {
            businesses.push({
              name: s.client_business,
              id: s.client_business_id,
              locations: [],
            });
          }

          if (
            // havent' seen this location yet, create it.
            businesses
              .find(b => b.id == s.client_business_id)
              .locations.filter(l => l.id == s.location_id).length < 1
          ) {
            businesses
              .find(b => b.id == s.client_business_id)
              .locations.push({
                name: s.location,
                id: s.location_id,
                shifts: [],
              });
          }

          // have the business and the location. add this shift to the location's collection
          businesses
            .find(b => b.id == s.client_business_id)
            .locations.find(l => l.id == s.location_id)
            .shifts.push(s);
        });

        return businesses;
      },
      sortedShifts: function() {
        return this.shifts.sort((a, b) => {
          let nameA = a.client_business.toLowerCase();
          let nameB = b.client_business.toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
      },
    },
    created() {
      this.setUp();
    },
    watch: {
      //'page': {
      //  handler(value) {
      //    if (value != this.$route.query.page) {
      //      this.reGetRecords()
      //    } else {
      //    }
      //  }
      //}
    },
    data: function() {
      return {
        limit: 40,
        page: 1,
        startDateMenu: false,
        endDateMenu: false,
        startDate: this.$moment()
          .subtract(2, 'weeks')
          .format('YYYY-MM-DD'),
        endDate: this.$moment()
          .subtract(1, 'weeks')
          .format('YYYY-MM-DD'),
        reportsPending: true,
        loadingCsv: false,
        shifts: [],
        clientAdmins: [],
        //defaultSort: {
        //  sortBy: 'order',
        //  rowsPerPage: -1, //all
        //},
        //headers: [
        //  { text: 'Shift Type', value: 'name', sortable: false },
        //  { text: 'Hours', value: 'hours', sortable: false },
        //  { text: 'Bonuses', value: null, sortable: false },
        //  { text: 'Reimbursements', value: null, sortable: false },
        //  { text: 'Notes', value: null, sortable: false },
        //],
      };
    },

    methods: {
      setUp: function() {
        this.startDate = this.$route.query.startDate || this.startDate;
        this.endDate = this.$route.query.endDate || this.endDate;
        this.page = parseInt(this.$route.query.page) || this.page;

        this.$router.push({
          name: 'billingReports',
          query: this.formattedQuery,
        });

        this.getReports();
      },
      endsNextDay: function(shift) {
        if (
          this.$moment(shift.start_datetime).day() !=
          this.$moment(shift.end_datetime).day()
        ) {
          return true;
        }
        return false;
      },
      bonusIcon: function(bonusType) {
        if (bonusType == 'Client-added') {
          return 'local_atm';
        } else if (bonusType == 'Travel') {
          return 'directions_car';
        } else if (bonusType == 'Call-in') {
          return 'phone';
        }
      },
      dateOrderedShifts: function(shifts) {
        let dates = [];

        shifts.forEach(s => {
          if (
            dates.filter(
              d =>
                d.date == this.$moment(s.start_datetime).format('YYYY-MM-DD'),
            ).length < 1
          ) {
            dates.push({
              date: this.$moment(s.start_datetime).format('YYYY-MM-DD'),
              shifts: [],
              dateObject: s.start_datetime,
            });
          }

          dates
            .find(
              d =>
                d.date == this.$moment(s.start_datetime).format('YYYY-MM-DD'),
            )
            .shifts.push(s);
        });

        return dates;
      },

      generateCsv: function(shifts) {
        const options = {
          fieldSeparator: ',',
          quoteStrings: '"',
          decimalSeparator: '.',
          showLabels: true,
          showTitle: false,
          filename: `billing-report_${this.$moment(this.startDate).format(
            'YYYY-MM-DD',
          )}--${this.$moment(this.endDate).format('YYYY-MM-DD')}`,
          useTextFile: false,
          useBom: true,
          useKeysAsHeaders: true,
        };

        const exportedFormat = shifts.report.map(s => {
          const row = {};

          row['Parent client'] = s.client_business;

          row['Client location'] = s.location;

          row['Service'] = 'Staffing';

          row['Position'] = s.position;

          row['Dif'] = s.dif;

          row['Employee'] = s.worker;

          row['Date'] = this.$moment(s.start_datetime).format('YYYY-MM-DD');

          row['Quantity'] = s.hours_worked;

          row['Client-added bonus'] = s.bonuses
            .filter(b => b.type == 'Client-added')
            .map(b => b.amount);
          row['Travel bonus'] = s.bonuses
            .filter(b => b.type == 'Travel')
            .map(b => b.amount);
          row['Call-in bonus'] = s.bonuses
            .filter(b => b.type == 'Call-in')
            .map(b => b.amount);

          // TODO: Cancellation columns

          return row;
        });

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(exportedFormat);
        this.loadingCsv = false;
      },

      round: function(hours) {
        return Math.round(hours * 100) / 100;
      },

      csvExport: function() {
        this.loadingCsv = true;
        this.$store
          .dispatch('reports/getBillingReport', {
            startDate: this.startDate,
            endDate: this.endDate,
          })
          .then(shifts => {
            this.generateCsv(shifts);
          })
          .catch(err => {
            console.log(err);
          })
          .finally(r => {
            this.loadingCsv = false;
          });
      },

      dateFormatted: function(date) {
        return date ? this.$moment(date).format('dddd, MMMM D') : '';
      },
      getReports: function() {
        this.reportsPending = true;
        this.$store
          .dispatch('reports/getBillingReport', this.formattedQuery)
          .then(report => {
            this.queryTotal = report.total;
            this.shifts = report.report;
            this.clientAdmins = report.clientAdmins;
          })
          .catch(err => {
            console.log(err);
          })
          .finally(res => {
            this.reportsPending = false;
          });
      },
      reGetRecords: function() {
        this.$router.push({
          name: 'billingReports',
          query: this.formattedQuery,
        });
      },
    },
  };
</script>

<style scoped>

</style>
